import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { browserControlWS } from '../services/browserControlWebSocket';
import { getIframeEventEmitter } from '../utils/iframeEventEmitter';
import { isInIframe } from '../utils/iframe';

/**
 * Hook для автоматического отслеживания навигации между страницами
 * Отправляет событие user-action с типом 'navigation' при изменении route
 * Также отправляет navigation-event в parent window если работаем в iframe
 */
export function useNavigationTracking() {
  const location = useLocation();
  const prevLocationRef = useRef(null);
  const isIframe = isInIframe();

  useEffect(() => {
    const prevPath = prevLocationRef.current?.pathname;
    const currentPath = location.pathname;

    // Пропустить первый рендер (когда prevPath еще null)
    if (prevPath === null) {
      prevLocationRef.current = location;
      return;
    }

    // Если маршрут изменился, отправить событие
    if (prevPath !== currentPath) {
      // Пропустить, если навигация инициирована агентом
      if (window.__agentCommandInProgress) {
        console.log('[useNavigationTracking] Skipping navigation event (agent command in progress)');
      } else {
        // Отправить событие в WebSocket
        browserControlWS.sendUserAction('navigation', {
          from: prevPath,
          to: currentPath,
          search: location.search,
        });

        // Если работаем в iframe, отправить событие в parent window
        if (isIframe) {
          const emitter = getIframeEventEmitter();
          const hash = window.location.hash.substring(1);
          const [pathname, search] = hash.split('?');
          const searchParams = new URLSearchParams(search || '');

          // Извлекаем carId если это страница /cars/:id
          const carIdMatch = currentPath.match(/^\/cars\/([^/]+)$/);
          const carId = carIdMatch ? carIdMatch[1] : null;

          emitter.emitNavigation({
            route: currentPath,
            from: prevPath,
            carId,
            queryParams: Object.fromEntries(searchParams),
            url: window.location.href,
          });

          console.log('[useNavigationTracking] Navigation event sent to parent window');
        }
      }
    }

    // Сохранить текущий location для следующего сравнения
    prevLocationRef.current = location;
  }, [location, isIframe]);
}
